import { useState } from 'react';
import { Size, assertMayEmulate, t, type EmulationTarget } from '@bms2/shared';
import { Button } from '../components/controls/Button';
import { ModalShell } from '../components/modals/ModalShell';
import styles from './shell.module.css';

/**
 * View as (piece 12, decision 0188). 🔒 The person emulated is seen and worked
 * as exactly, so the picker lists the employments rather than the people: a
 * person holding two jobs is two different viewers. 🔨 A super admin cannot
 * emulate another super admin, and the row says so rather than vanishing —
 * `assertMayEmulate` refuses the choice either way.
 */
export interface EmulationTargetRow extends EmulationTarget {
  companyName: string;
  roleName: string | null;
  title: string | null;
}

export interface ViewAsModalProps {
  open: boolean;
  /** The real person, whose own employments are not offered. */
  actor: { personId: string; isSuperAdmin: boolean };
  rows: readonly EmulationTargetRow[];
  onChoose: (target: EmulationTargetRow) => void;
  onCancel: () => void;
  openerId?: string | null;
}

export function ViewAsModal({ open, actor, rows, onChoose, onCancel, openerId = null }: ViewAsModalProps) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // A closed picker holds no choice: the selection is derived from `open` rather
  // than reset back into state when it closes.
  const target = open ? (rows.find((row) => row.employmentId === chosen) ?? null) : null;

  const confirm = () => {
    if (!target) return;
    try {
      assertMayEmulate(actor, target);
      setRefusal(null);
      onChoose(target);
    } catch (error) {
      // silent: the refusal's own sentence is shown; a refusal writes no row.
      setRefusal(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <ModalShell
      open={open}
      title={t('shell.viewAs')}
      subtitle={t('shell.viewAsSubtitle')}
      onCancel={() => {
        setChosen(null);
        setRefusal(null);
        onCancel();
      }}
      dataModal="view-as"
      openerId={openerId}
      footer={
        <>
          <Button size={Size.Medium} text={[t('shell.viewAsConfirm')]} actions={[confirm]} disabled={target === null} />
          <Button size={Size.Medium} text={[t('modal.cancel')]} actions={[onCancel]} />
        </>
      }
    >
      <div data-view-as-list>
        {rows.length === 0 && <p>{t('shell.viewAsEmpty')}</p>}
        {rows.map((row) => (
          <label key={row.employmentId} className={styles.queueRow} data-view-as-row={row.employmentId}>
            <input
              type="radio"
              name="view-as"
              value={row.employmentId}
              checked={target?.employmentId === row.employmentId}
              onChange={() => setChosen(row.employmentId)}
              disabled={row.isSuperAdmin || row.personId === actor.personId}
            />
            <span>
              {row.personName}
              <span className={styles.feedWhen}>
                {[row.title, row.roleName, row.companyName].filter(Boolean).join(' · ')}
                {row.isSuperAdmin ? ` — ${t('shell.viewAsSuperAdmin')}` : ''}
              </span>
            </span>
          </label>
        ))}
      </div>
      {refusal && <p role="alert" data-view-as-refusal>{refusal}</p>}
    </ModalShell>
  );
}
